// Initialize application before any other imports
// This helps ensure version information is properly set up
import "./utils/version";
// This import specifically handles the Immer .version property error
import "./utils/immerPatch";
import React from "react";
import { createRoot } from "react-dom/client";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";

// Import your main application component
import App from "./App";
import { getVersion } from "./utils/version";

// Single emotion cache owned by the entry bundle so lazy routes share it.
const emotionCache = createCache({ key: "css" });

// ============================================================================
// FIX: Suppress Chrome Extension Errors
// ============================================================================
// Chrome extensions (React DevTools, Grammarly, password managers, etc.) 
// sometimes throw errors that clutter the console. These are NOT bugs in our code.
(() => {
  // Suppress runtime.lastError from browser extensions
  window.addEventListener('error', (event) => {
    const message = event.message || '';
    // Suppress extension-related errors that we can't control
    if (
      message.includes('runtime.lastError') ||
      message.includes('message port closed') ||
      message.includes('back/forward cache') ||
      message.includes('extension port') ||
      message.includes('FrameDoesNotExist')
    ) {
      event.stopImmediatePropagation();
      event.preventDefault();
      return false;
    }
  }, true); // Use capture phase to catch early
})();

// ============================================================================
// FIX: Make Event Listeners Passive by Default
// ============================================================================
// This fixes "[Violation] Added non-passive event listener to scroll-blocking event"
// warnings that appear when libraries (Ant Design, React Flow, Monaco) add touch/wheel
// listeners without the passive flag
(() => {
  if (typeof EventTarget !== 'undefined') {
    const originalAddEventListener = EventTarget.prototype.addEventListener;
    EventTarget.prototype.addEventListener = function (
      type: string,
      listener: any,
      options?: any
    ) {
      // Make touch and wheel events passive by default for better scroll performance
      if (['touchstart', 'touchmove', 'wheel', 'mousewheel'].includes(type)) {
        if (typeof options === 'object' && options !== null) {
          // Only set passive if not explicitly set to false
          if (options.passive === undefined) {
            options.passive = true;
          }
        } else if (typeof options === 'boolean') {
          // Convert boolean capture option to object with passive
          options = { capture: options, passive: true };
        } else {
          // No options provided, add passive
          options = { passive: true };
        }
      }
      return originalAddEventListener.call(this, type, listener, options);
    };
  }
})();

// Setup enhanced logging for debugging
(() => {
  const logStyles = {
    info: 'background: #2196F3; color: white; padding: 2px 5px; border-radius: 3px;',
    error: 'background: #F44336; color: white; padding: 2px 5px; border-radius: 3px;',
    warn: 'background: #FF9800; color: white; padding: 2px 5px; border-radius: 3px;',
  };

  // Save original console methods
  const originalConsole = {
    log: console.log,
    error: console.error,
    warn: console.warn,
    info: console.info,
  };

  // Enhance console methods to be more visible
  console.log = function(...args) {
    originalConsole.log('%c[LOG]', logStyles.info, ...args);
  };
  
  console.error = function(...args) {
    originalConsole.error('%c[ERROR]', logStyles.error, ...args);
  };
  
  console.warn = function(...args) {
    originalConsole.warn('%c[WARN]', logStyles.warn, ...args);
  };
  
  console.info = function(...args) {
    originalConsole.info('%c[INFO]', logStyles.info, ...args);
  };

  // Log useful startup information
  console.info('Application starting up', {
    environment: process.env.NODE_ENV,
    version: window.DATACORD_VERSION,
    userAgent: navigator.userAgent,
    timestamp: new Date().toISOString(),
  });

  // Add global error handler for real application errors (not extension errors)
  window.addEventListener('error', (event) => {
    const message = event.message || '';
    // Don't log extension errors (already suppressed above)
    if (
      !message.includes('runtime.lastError') &&
      !message.includes('message port closed') &&
      !message.includes('back/forward cache') &&
      !message.includes('extension port') &&
      !message.includes('FrameDoesNotExist')
    ) {
      console.error('Uncaught error:', event.error);
    }
  });

  // Add unhandled promise rejection handler
  window.addEventListener('unhandledrejection', (event) => {
    console.error('Unhandled Promise rejection:', event.reason);
  });
})();

// Enhanced logging for debugging
console.info(`Starting application: Datacord UI - Version ${getVersion()}`);

const rootElement = document.getElementById("root");
if (!rootElement) {
  throw new Error("Failed to find the root element");
}

const root = createRoot(rootElement);
root.render(
  <React.StrictMode>
    <CacheProvider value={emotionCache}>
      <App />
    </CacheProvider>
  </React.StrictMode>
);
// Force rebuild Thu Oct 16 19:00:47 PDT 2025
